import { CoordinateSpace, type ICoordinateSpace, type InFullSizeImage, type InTransformed, type InWindow } from "./CoordinateSpace";
import type { IPoint } from "./Point";
import type { IRegion } from "./Region";
import type { ITransformation } from "./Transformation";
import type { IVector } from "./Vector";

export interface ILine<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> {
	readonly coordinateSpace: TCoordinateSpace;
	readonly from: IPoint<TCoordinateSpace>;
	readonly to: IPoint<TCoordinateSpace>;

	length(): number;

	toVector(): IVector<TCoordinateSpace>;

	/**
	 * Returns whether or not this line intersects with the other line
	 * This methods returns FALSE in the following scenarios:
	 *  - One of the lines has length 0
	 *  - The lines are parallel
	 *  - The lines do not intersect
	 */
	intersectsWithLine(otherFrom: IPoint<TCoordinateSpace>, otherTo: IPoint<TCoordinateSpace>): boolean;

	/**
	 * Returns whether or not this line intersects with the provided region
	 *
	 * ** NOTE**
	 * This only returns true when the line intersects with the region boundary.
	 * If the line is entirely contained within the region, this method will return false
	 */
	intersectsWithRegion(region: IRegion<TCoordinateSpace>): boolean;

	/**
	 * Returns the point at which this line intersects with the other line.
	 * ** NOTE **
	 * This methods returns NULL in the following scenarios:
	 *  - One of the lines has length 0
	 *  - The lines are parallel
	 *  - The lines do not intersect
	 */
	getIntersectionPointWithLine(other: ILine<TCoordinateSpace>): IPoint<TCoordinateSpace> | null;
}

export abstract class Line<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> implements ILine<TCoordinateSpace> {
	abstract readonly coordinateSpace: TCoordinateSpace;

	protected constructor(
		public readonly from: IPoint<TCoordinateSpace>,
		public readonly to: IPoint<TCoordinateSpace>,
	) {}

	length() {
		const { from, to } = this;
		const { x: x1, y: y1 } = from;
		const { x: x2, y: y2 } = to;
		const dx = x2 - x1;
		const dy = y2 - y1;
		return Math.hypot(dx, dy);
	}

	toVector() {
		return this.coordinateSpace.createVector(this.to.x - this.from.x, this.to.y - this.from.y);
	}

	intersectsWithLine(otherFrom: IPoint<TCoordinateSpace>, otherTo: IPoint<TCoordinateSpace>): boolean {
		const { from, to } = this;

		const { x: x1, y: y1 } = from;
		const { x: x2, y: y2 } = to;
		const { x: x3, y: y3 } = otherFrom;
		const { x: x4, y: y4 } = otherTo;

		// Check if none of the lines are of length 0
		if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
			return false;
		}

		// Check if lines are parallel
		const determinant = (x2 - x1) * (y4 - y3) - (x4 - x3) * (y2 - y1);
		if (determinant === 0) {
			return false;
		}

		// Calculate how far along the lines we need to travel to reach the intersection point
		const lambda = ((y4 - y3) * (x4 - x1) + (x3 - x4) * (y4 - y1)) / determinant;
		if (lambda < 0 || lambda > 1) return false;

		const gamma = ((y1 - y2) * (x4 - x1) + (x2 - x1) * (y4 - y1)) / determinant;

		// Check if the intersection happens along the lines or outside the lines
		return gamma >= 0 && gamma <= 1;
	}

	intersectsWithRegion(region: IRegion<TCoordinateSpace>): boolean {
		const { topLeft, topRight, bottomLeft, bottomRight } = region;
		return (
			this.intersectsWithLine(topLeft, topRight) ||
			this.intersectsWithLine(topRight, bottomRight) ||
			this.intersectsWithLine(bottomRight, bottomLeft) ||
			this.intersectsWithLine(bottomLeft, topLeft)
		);
	}

	getIntersectionPointWithLine(other: ILine<TCoordinateSpace>): IPoint<TCoordinateSpace> | null {
		const { from, to } = this;
		const { from: otherFrom, to: otherTo } = other;

		const { x: x1, y: y1 } = from;
		const { x: x2, y: y2 } = to;
		const { x: x3, y: y3 } = otherFrom;
		const { x: x4, y: y4 } = otherTo;

		// Check if none of the lines are of length 0
		if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
			return null;
		}

		const determinant = (x2 - x1) * (y4 - y3) - (x4 - x3) * (y2 - y1);

		// Check if lines are parallel
		if (determinant === 0) {
			return null;
		}

		// Calculate how far along the lines we need to travel to reach the intersection point
		const lambda = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / determinant;
		const gamma = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / determinant;

		// Check if the intersection happens along the lines or outside the lines
		if (lambda < 0 || lambda > 1 || gamma < 0 || gamma > 1) {
			return null;
		}

		// Return the x and y coordinates of the intersection
		const x = x1 + lambda * (x2 - x1);
		const y = y1 + lambda * (y2 - y1);

		return this.coordinateSpace.createPoint(x, y);
	}
}

export interface ILineInWindow extends ILine<InWindow> {}

export interface ILineInFullSizeImage extends ILine<InFullSizeImage> {}

export interface ILineInTransformedFullSizeImage extends ILine<InTransformed<InFullSizeImage>> {}

export class LineInWindow extends Line<InWindow> implements ILineInWindow {
	readonly coordinateSpace: InWindow = CoordinateSpace.inWindow;

	constructor(from: IPoint<InWindow>, to: IPoint<InWindow>) {
		super(from, to);
	}
}

export class LineInFullSizeImage extends Line<InFullSizeImage> implements ILineInFullSizeImage {
	readonly coordinateSpace: InFullSizeImage = CoordinateSpace.inFullSizeImage;

	constructor(from: IPoint<InFullSizeImage>, to: IPoint<InFullSizeImage>) {
		super(from, to);
	}
}

export class LineInTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> extends Line<InTransformed<TCoordinateSpace>> {
	readonly coordinateSpace: InTransformed<TCoordinateSpace>;

	constructor(
		private readonly originalCoordinateSpace: ICoordinateSpace<TCoordinateSpace>,
		transformation: ITransformation<TCoordinateSpace>,
		from: IPoint<InTransformed<TCoordinateSpace>>,
		to: IPoint<InTransformed<TCoordinateSpace>>,
	) {
		super(from, to);
		this.coordinateSpace = from.coordinateSpace;
	}
}

export class LineInTransformedFullSizeImage extends LineInTransformed<InFullSizeImage> implements ILineInTransformedFullSizeImage {
	constructor(transformation: ITransformation<InFullSizeImage>, from: IPoint<InTransformed<InFullSizeImage>>, to: IPoint<InTransformed<InFullSizeImage>>) {
		super(CoordinateSpace.inFullSizeImage, transformation, from, to);
	}
}
